import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Button } from 'components/Button';
import { Code } from 'components/Code';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { emitMessage } from 'engine/api';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import {
	AutomaticValidationOverrideSchema,
	GameRoundOverrideSchema,
	GameTaskOverrideSchema,
	GameVariablesOverrideSchema,
	MultipleChoiceAnswerOverrideSchema,
	OpenAnswerOverrideSchema,
	VariantRecordSchema,
	VariantSchema,
	type GameRoundOverride,
	type Variant,
	type VariantRecord,
} from 'shared/modelTypes/variant';
import { VariantForm } from 'forms/VariantForm';
import type { Game, GameRecord, GameRound } from 'shared/modelTypes/game';
import type { SkinRecord } from 'shared/modelTypes/skin';
import { FilePickerProvider } from 'components/FilePickerProvider';

function removeDeletedVariantRounds(variantRounds: GameRoundOverride[], gameRounds: GameRound[]) {
	const roundMap = new Map(gameRounds.map((r) => [r.id, r]));

	for (let i = variantRounds.length - 1; i >= 0; i--) {
		const variantRound = variantRounds[i];
		const matchingRound = roundMap.get(variantRound.id);

		if (!matchingRound) {
			variantRounds.splice(i, 1);
		} else if (variantRound.tasks && matchingRound.tasks) {
			const validTaskIds = new Set(matchingRound.tasks.map((t) => t.id));
			variantRound.tasks = variantRound.tasks.filter((t) => validTaskIds.has(t.id));
		}
	}
}

function addMissingVariantRounds(variantRounds: GameRoundOverride[], gameRounds: GameRound[]) {
	for (const gameRound of gameRounds) {
		let variantRound = variantRounds.find((r) => r.id === gameRound.id);
		if (!variantRound) {
			variantRound = GameRoundOverrideSchema.parse({ id: gameRound.id });
			variantRounds.push(variantRound);
		}

		// Add all tasks from base game to variant round
		for (const gameTask of gameRound.tasks) {
			let variantTask = variantRound.tasks.find((t) => t.id === gameTask.id);
			if (!variantTask) {
				const newVariantTask = GameTaskOverrideSchema.parse({ id: gameTask.id });
				variantRound.tasks.push(newVariantTask);
				variantTask = newVariantTask;
			}

			// If the base task has automatic validation, populate the override with its answers
			if (gameTask.typeAutomaticValidationConfig) {
				const baseConfig = gameTask.typeAutomaticValidationConfig;
				let overrideConfig = variantTask?.typeAutomaticValidationConfig;
				if (!overrideConfig) {
					overrideConfig = AutomaticValidationOverrideSchema.parse({});
				}
				// derive ordering and id's from base game, but use values from the override where possible
				overrideConfig.multipleChoiceAnswers = baseConfig.multipleChoiceAnswers?.map((base) => {
					const override = overrideConfig.multipleChoiceAnswers?.find((m) => m.id == base.id);
					return override ?? MultipleChoiceAnswerOverrideSchema.parse({ id: base.id });
				});
				// derive ordering and id's from base game, but use values from the override where possible
				overrideConfig.openAnswers = baseConfig.openAnswers?.map((base) => {
					const override = overrideConfig.openAnswers?.find((m) => m.id == base.id);
					return override ?? OpenAnswerOverrideSchema.parse({ id: base.id });
				});
			}
		}
	}
}

export const Route = createFileRoute('/dashboard/variants/edit/$id')({
	component: RouteComponent,
	loader: async ({ params }) => {
		const variantRes = await emitMessage({ kind: 'variant-find', id: params.id });
		const gameId = variantRes.variant?.gameId;
		const gameRes = gameId ? await emitMessage({ kind: 'game-find', id: gameId }) : undefined;

		let variant: VariantRecord | undefined;

		if (variantRes.variant) {
			variant = VariantRecordSchema.parse(variantRes.variant);

			const gameVariables = gameRes?.game?.game.variables;

			if (gameVariables) {
				const variantVariables = variant.variant.gameOverrides.variables;
				variant.variant.gameOverrides.variables = gameVariables.map((gameVariable) => {
					const variantVariable = variantVariables.find((v) => v.key === gameVariable.key);
					if (variantVariable) {
						return variantVariable;
					} else {
						// create a new variable override
						return GameVariablesOverrideSchema.parse({ key: gameVariable.key });
					}
				});
			}

			const gameRounds = gameRes?.game?.game.rounds;
			if (gameRounds) {
				addMissingVariantRounds(variant.variant.gameOverrides.rounds, gameRounds);
			}
			removeDeletedVariantRounds(variant.variant.gameOverrides.rounds, gameRounds || []);
		}

		return {
			variantsResponse: variant,
			gamesResponse: await emitMessage({ kind: 'game-list' }),
			skinsResponse: await emitMessage({ kind: 'skin-list' }),
			baseGame: gameRes?.game,
			directory: (await emitMessage({ kind: 'media-list-directory', folderId: null })).media,
		};
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();

	if (!data.variantsResponse) {
		return <span>Variant not found</span>;
	}

	if (!data.directory) {
		return <span>No directory found</span>;
	}

	return (
		<div>
			{!data.variantsResponse.variant && <span>Variant not found</span>}
			<FilePickerProvider startDirectory={data.directory}>
				{data.variantsResponse.variant && (
					<VariantUpdateForm
						variant={data.variantsResponse!}
						games={data.gamesResponse.games!}
						baseGame={data.baseGame?.game}
						skins={data.skinsResponse.skins ?? []}
					/>
				)}
			</FilePickerProvider>
		</div>
	);
}

function VariantUpdateForm({
	games,
	variant,
	baseGame,
	skins,
}: {
	games: GameRecord[];
	variant: VariantRecord;
	baseGame?: Game;
	skins: SkinRecord[];
}) {
	const methods = useForm<Variant>({
		// @ts-expect-error zodResolver type inference seems to be bad
		resolver: zodResolver(VariantSchema),
		defaultValues: variant.variant,
	});

	const err = methods.formState.errors;

	const router = useRouter();
	const [result, setResult] = useState<Variant | undefined>();
	const [res, setRes] = useState<Promise<unknown> | undefined>();

	const gameOptions = games.map((g) => ({ value: g.id, label: g.game.name }));

	const onSubmit = async (data: Variant) => {
		setResult(data);

		const r = emitMessage({
			kind: 'variant-update',
			variant: { id: variant.id, gameId: data.gameId, variant: data },
		});
		setRes(r);
		const response = await r;
		if (response.success) {
			router.invalidate();
			router.navigate({ to: '/dashboard/variants' });
		} else {
			setRes(undefined);
			toast('server did not save variant', { icon: <X /> });
		}
	};

	console.log('errors', err);
	return (
		<FormProvider {...methods}>
			<form
				style={{ opacity: res ? '0.4' : '1' }}
				className="flex flex-col gap-3"
				// @ts-expect-error zodResolver type inference seems to be bad
				onSubmit={methods.handleSubmit(onSubmit)}
			>
				<VariantForm
					label="Edit Variant"
					gameOptions={gameOptions}
					withGameOverrides
					baseGame={baseGame}
					skinOptions={skins.map((s) => ({ value: s.id, label: s.skin.name }))}
				/>
				<Button type="submit">Submit</Button>
			</form>
			{result && (
				<Code block className="mt-4">
					{JSON.stringify(result, null, 2)}
				</Code>
			)}
		</FormProvider>
	);
}
